import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getConsent, saveConsent } from "@/lib/analytics";

/**
 * Cookie consent for Google Ads measurement. Nothing non-essential is granted until the
 * visitor chooses, and both choices are given the same prominence.
 */
export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const panel = useRef(null);

  useEffect(() => {
    const stored = getConsent();
    setCurrent(stored);
    if (!stored) setOpen(true);
    const reopen = () => {
      setCurrent(getConsent());
      setOpen(true);
    };
    window.addEventListener("pg:cookie-settings", reopen);
    return () => window.removeEventListener("pg:cookie-settings", reopen);
  }, []);

  // While the banner is showing it must not sit on top of anything the visitor needs to reach.
  useEffect(() => {
    const root = document.documentElement;
    const height = open && panel.current ? panel.current.offsetHeight : 0;
    root.style.setProperty("--pg-consent-height", `${height}px`);
    return () => root.style.setProperty("--pg-consent-height", "0px");
  }, [open, current]);

  const choose = (granted) => {
    saveConsent(granted);
    setCurrent(getConsent());
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      ref={panel}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie choices"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-pg-border bg-white/95 backdrop-blur-md shadow-[0_-4px_24px_rgba(15,23,42,0.08)]"
    >
      <div className="pg-container flex flex-col gap-3 py-3 sm:gap-4 sm:py-4 lg:flex-row lg:items-center lg:justify-between lg:gap-5 lg:py-5">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold text-pg-dark">Your cookie choices</p>
          <p className="mt-1 text-[13px] leading-relaxed text-pg-muted sm:hidden">
            Essential cookies keep the site working. With your permission we also measure how many
            people book after seeing one of our adverts. We never profile you or sell your details.{" "}
            <Link to="/cookie-policy" className="pg-link font-semibold">Cookie Policy</Link>
            {" · "}
            <Link to="/privacy-policy" className="pg-link font-semibold">Privacy Policy</Link>
          </p>
          <p className="mt-1.5 hidden text-sm leading-relaxed text-pg-muted sm:block">
            We use essential cookies to make this website work. With your permission we also use
            Google Ads cookies to measure how many people book an inspection after seeing one of
            our adverts. We do not use cookies to profile you or sell your details, and you can
            change your mind at any time.{" "}
            <Link to="/cookie-policy" className="pg-link font-semibold">
              Cookie Policy
            </Link>{" "}
            ·{" "}
            <Link to="/privacy-policy" className="pg-link font-semibold">
              Privacy Policy
            </Link>
          </p>
          {current && (
            <p className="mt-1.5 text-xs text-pg-muted" data-testid="cookie-current-choice">
              Your current choice:{" "}
              {current.granted ? "measurement cookies accepted" : "essential cookies only"}.
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:flex sm:flex-row sm:gap-3 lg:shrink-0">
          <button
            type="button"
            onClick={() => choose(false)}
            data-testid="cookie-essential-only"
            className="min-h-[48px] rounded-md border border-pg-primary px-4 text-sm font-semibold text-pg-primary transition-colors hover:bg-pg-pale sm:min-h-0 sm:px-6 sm:py-3"
          >
            Essential only
          </button>
          <button
            type="button"
            onClick={() => choose(true)}
            data-testid="cookie-accept-all"
            className="min-h-[48px] rounded-md border border-pg-primary bg-pg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-pg-dark sm:min-h-0 sm:px-6 sm:py-3"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
